import { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { enablePush, syncPush, pushSupported, permission, isIOS, isStandalone, registerSW } from "@/lib/push";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Bell, BellRing, BellOff, Share, PlusSquare, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { tx } from "@/i18n";

let synced = null;

const IosHelp = ({ open, onOpenChange }) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent data-testid="push-ios-help">
      <DialogHeader>
        <DialogTitle className="font-display">{tx("Meldingen op iPhone")}</DialogTitle>
        <DialogDescription>{tx("Op iPhone werken meldingen alleen als je Costa Key op je beginscherm zet.")}</DialogDescription>
      </DialogHeader>
      <ol className="space-y-3 text-sm">
        <li className="flex gap-2"><Share className="h-4 w-4 shrink-0 mt-0.5 text-primary" /> {tx("Tik in Safari onderaan op de Deel-knop.")}</li>
        <li className="flex gap-2"><PlusSquare className="h-4 w-4 shrink-0 mt-0.5 text-primary" /> {tx("Kies 'Zet op beginscherm' en tik op 'Voeg toe'.")}</li>
        <li className="flex gap-2"><Bell className="h-4 w-4 shrink-0 mt-0.5 text-primary" /> {tx("Open Costa Key vanaf je beginscherm, log in en tik op 'Meldingen aanzetten'.")}</li>
      </ol>
    </DialogContent>
  </Dialog>
);

export const PushToggle = () => {
  const [state, setState] = useState("idle");
  const [help, setHelp] = useState(false);

  useEffect(() => {
    if (!synced) synced = registerSW().then(() => syncPush());
    synced.then((ok) => ok && setState("on"));
    if (permission() === "denied") setState("denied");
    const on = () => setState("on");
    window.addEventListener("push-enabled", on);
    return () => window.removeEventListener("push-enabled", on);
  }, []);

  const needsInstall = isIOS && !isStandalone();
  if (!needsInstall && !pushSupported()) return null;

  const turnOn = async () => {
    if (needsInstall) { setHelp(true); return; }
    setState("busy");
    try {
      await enablePush();
      setState("on");
      toast.success(tx("Meldingen staan aan op dit apparaat"));
    } catch (e) {
      const denied = e.message === "denied";
      setState(denied ? "denied" : "idle");
      toast.error(denied ? tx("Meldingen zijn geblokkeerd. Sta ze toe in de instellingen van je browser of telefoon.") : apiError(e));
    }
  };
  const test = async () => {
    try { await api.post("/push/test"); toast.success(tx("Testmelding verstuurd")); } catch (e) { toast.error(apiError(e)); }
  };

  if (state === "on") return (
    <Button variant="ghost" onClick={test} className="w-full justify-start gap-3 text-secondary" data-testid="push-on-btn" title={tx("Stuur een testmelding")}>
      <BellRing className="h-[18px] w-[18px]" /> {tx("Meldingen staan aan")}
    </Button>
  );
  return (
    <>
      <Button variant="ghost" onClick={turnOn} disabled={state === "busy"} className={`w-full justify-start gap-3 ${state === "denied" ? "text-muted-foreground" : "text-primary"}`} data-testid="push-enable-btn">
        {state === "busy" ? <Loader2 className="h-[18px] w-[18px] animate-spin" /> : state === "denied" ? <BellOff className="h-[18px] w-[18px]" /> : <Bell className="h-[18px] w-[18px]" />}
        {state === "denied" ? tx("Meldingen geblokkeerd") : tx("Meldingen aanzetten")}
      </Button>
      <IosHelp open={help} onOpenChange={setHelp} />
    </>
  );
};
